// novem atom — KPI / stat card (NovemHR-ekranlar port, Faz 0.2).
//
// The dashboard-grade metric card from the novem design: tinted icon
// chip top-left, optional delta top-right, serif value, sans label.
// Mirrors the mockup's StatCard (novem-core.jsx) with the library's
// conventions: data-attribute style hooks + caller-supplied content
// (the host owns i18n keys; this component owns layout + semantics).
//
// Accessibility: the card is a <div> (non-interactive). Value + label
// read naturally in document order ("24 — Bekleyen onay"). The delta
// carries an aria-label slot for screen-reader-friendly phrasing
// ("+6 today, increasing") because "+6" alone is ambiguous; caller
// supplies it via deltaLabel when the visual delta is terse.

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

/** Tint families for the icon chip — map to the state ramps. */
export type StatCardTone = "accent" | "success" | "warning" | "danger" | "info" | "neutral";

/** Direction drives the delta's color (up=success, down=danger, flat=muted). */
export type StatCardDeltaDirection = "up" | "down" | "flat";

export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Metric label (e.g. t("dashboard.kpi.pending_approvals")). */
  readonly label: ReactNode;
  /** The headline value — serif, large. String/number/element. */
  readonly value: ReactNode;
  /** Optional icon element (e.g. a lucide icon) shown in the tinted chip. */
  readonly icon?: ReactNode;
  /** Icon chip tint. Defaults to 'accent' (emerald). */
  readonly tone?: StatCardTone;
  /** Optional delta text (e.g. "+6 bugün", "−3"). */
  readonly delta?: ReactNode;
  /** Visual direction of the delta. Defaults to 'flat' (muted). */
  readonly deltaDirection?: StatCardDeltaDirection;
  /**
   * Screen-reader phrasing for the delta when the visual text is terse
   * ("+6" → "6 more than yesterday, increasing"). Rendered as aria-label
   * on the delta span.
   */
  readonly deltaLabel?: string;
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      label,
      value,
      icon,
      tone = "accent",
      delta,
      deltaDirection = "flat",
      deltaLabel,
      className,
      ...rest
    },
    ref,
  ) => (
    <div
      ref={ref}
      data-component="stat-card"
      className={cx("nai-stat-card", className)}
      {...rest}
    >
      {(icon !== undefined || delta !== undefined) && (
        <div className="nai-stat-card-top">
          {icon !== undefined ? (
            <div className="nai-stat-card-icon" data-tone={tone} aria-hidden="true">
              {icon}
            </div>
          ) : (
            <span />
          )}
          {delta !== undefined && (
            <span
              className="nai-stat-card-delta"
              data-direction={deltaDirection}
              aria-label={deltaLabel}
            >
              {delta}
            </span>
          )}
        </div>
      )}
      <div className="nai-stat-card-value">{value}</div>
      <div className="nai-stat-card-label">{label}</div>
    </div>
  ),
);
StatCard.displayName = "StatCard";
